import { DevicesCategory, Session } from '@app/api';
import { CheckboxBlockField, CheckboxBlockList } from '@app/form';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { useTranslation } from 'react-i18next';
import { useCallback, useState } from 'react';
import { IconChip } from '@app/core';
import { DEVICE_CATEGORY_ICONS } from './TechnicalRequirements';

interface Props {
  session?: Session;
  onDeviceChange: (device: DevicesCategory | null) => void;
}

const useStyles = createUseStyles((theme: Theme) => ({
  listContainer: {
    flexWrap: 'nowrap',
    gap: theme.marginBase * 2,
    marginRight: 0,
  },
  checkbox: {
    flex: 1,
    minWidth: 0,
    maxWidth: '100%',
    marginRight: 0,
  },
  labelInline: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase,
    marginBottom: theme.marginBase,
  },
  dangerText: {
    ...theme.fonts.label,
    color: theme.colors.redAccessible,
  },
}));

export const MeetingDeviceSelection = ({ session, onDeviceChange }: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();
  const [selectedDevice, setSelectedDevice] = useState<DevicesCategory | null>(
    null,
  );

  const handleDeviceChange = useCallback(
    (device: DevicesCategory) => {
      setSelectedDevice(device);
      onDeviceChange(device);
    },
    [onDeviceChange],
  );

  return (
    <div>
      <CheckboxBlockList
        label={
          <div className={classes.labelInline}>
            {t('booking.technicalRequirements.selectDevice')}
            <IconChip icon="ri-error-warning-line" color="red">
              <span className={classes.dangerText}>
                {t('booking.technicalRequirements.deviceChoiceDefinitive')}
              </span>
            </IconChip>
          </div>
        }
        noMargin
        fullLine
        classNameList={classes.listContainer}
        checkboxClassName={classes.checkbox}
      >
        {session?.availableDevicesCategories?.map((device) => (
          <CheckboxBlockField
            key={device}
            value={device}
            variant="green"
            label={t(
              `booking.technicalRequirements.allowedDevicesCategories.${device}`,
            )}
            type={'radio'}
            onChange={(_e) => handleDeviceChange(device)}
            checked={selectedDevice === device}
            icon={DEVICE_CATEGORY_ICONS[device]}
          />
        ))}
      </CheckboxBlockList>
    </div>
  );
};
